// src/components/coordinateGrid/EquationInputWidget.tsx
/**
 * EquationInputWidget Component
 * 
 * Floating widget for entering equations.
 * Two input modes:
 * 1. Expression mode: Type "y = 2x + 3"
 * 2. Coefficient mode: Enter m and c separately
 * 
 * Pattern follows existing ExpressionWidget from expressionsSlice.ts
 * 
 * Phase 2: Graphing Equations
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { addEquation, updateEquation } from '../../store/slices/equationsSlice';
import { parseEquation } from '../../lib/coordinateGrid/equationParser';

interface EquationInputWidgetProps {
  isVisible: boolean;
  position: { x: number; y: number };
  mode: 'create' | 'edit';
  initialExpression?: string;
  equationId?: string;
  onSubmit: (expression: string) => void;
  onCancel: () => void;
}

export const EquationInputWidget: React.FC<EquationInputWidgetProps> = ({
  isVisible,
  position,
  mode,
  initialExpression = '',
  equationId,
  onSubmit,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const [localExpression, setLocalExpression] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Sync with props
  useEffect(() => {
    if (isVisible) {
      setLocalExpression(initialExpression);
      validateExpression(initialExpression);
      
      // Focus input when widget becomes visible
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setLocalExpression('');
      setValidationError(null);
      setIsValid(false);
    }
  }, [isVisible, initialExpression]);

  // Validate expression
  const validateExpression = (expression: string) => {
    if (!expression.trim()) {
      setValidationError(null);
      setIsValid(false);
      return;
    }

    const parsed = parseEquation(expression.trim());
    
    if (!parsed.isValid) {
      setValidationError(parsed.error || 'Invalid equation format');
      setIsValid(false);
    } else {
      setValidationError(null);
      setIsValid(true);
    }
  };

  // Handle expression input changes
  const handleExpressionChange = (value: string) => {
    setLocalExpression(value);
    validateExpression(value);
  };

  // Handle saving the equation
  const handleSave = () => {
    if (!localExpression.trim() || !isValid) {
      return;
    }

    if (mode === 'edit' && equationId) {
      // Update existing equation
      dispatch(updateEquation({
        id: equationId,
        updates: { expression: localExpression.trim() }
      }));
    } else {
      // Create new equation
      dispatch(addEquation({
        expression: localExpression.trim()
      }));
    }

    onSubmit(localExpression.trim());
  };

  // Handle canceling
  const handleCancel = () => {
    onCancel();
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isValid) {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  if (!isVisible) {
    return null;
  }

  return createPortal(
    <div
      ref={widgetRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 10000,
        backgroundColor: '#FFFFFF',
        border: validationError 
          ? '2px solid #EF4444' 
          : isValid 
            ? '2px solid #10B981' 
            : '2px solid #D1D5DB',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        minWidth: '300px',
        maxWidth: '400px',
      }}
    >
      {/* Header */}
      <div style={{
        marginBottom: '12px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#111827',
      }}>
        {mode === 'edit' ? 'Edit Equation' : 'Add Equation'}
      </div>

      {/* Input field */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="equation-input"
          style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
          }}
        >
          Equation
        </label>
        <input
          ref={inputRef}
          id="equation-input"
          type="text"
          value={localExpression}
          onChange={(e) => handleExpressionChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g., y = 2x + 3"
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3B82F6';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#D1D5DB';
          }}
        />
        
        {/* Validation feedback */}
        {validationError && (
          <div style={{
            marginTop: '6px',
            fontSize: '12px',
            color: '#EF4444',
          }}>
            {validationError}
          </div>
        )}
        
        {isValid && !validationError && localExpression.trim() && (
          <div style={{
            marginTop: '6px',
            fontSize: '12px',
            color: '#10B981',
          }}>
            ✓ Valid equation
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        justifyContent: 'flex-end',
        marginTop: '16px'
      }}>
        <button
          onClick={handleCancel}
          style={{
            padding: '6px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            backgroundColor: '#FFFFFF',
            color: '#374151',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#FFFFFF';
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!localExpression.trim() || !isValid}
          style={{
            padding: '6px 12px',
            fontSize: '14px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: (!localExpression.trim() || !isValid) ? '#9CA3AF' : '#3B82F6',
            color: '#FFFFFF',
            cursor: (!localExpression.trim() || !isValid) ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            if (localExpression.trim() && isValid) {
              e.currentTarget.style.backgroundColor = '#2563EB';
            }
          }}
          onMouseLeave={(e) => {
            if (localExpression.trim() && isValid) {
              e.currentTarget.style.backgroundColor = '#3B82F6';
            }
          }}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>

      {/* Help text */}
      <div style={{ 
        marginTop: '12px',
        fontSize: '11px',
        color: '#9CA3AF',
        borderTop: '1px solid #F3F4F6',
        paddingTop: '8px'
      }}>
        <strong>Examples:</strong> y = 2x + 3, y = x² + 1, x = 5, y = -2
        <br />
        Press Enter to save, Esc to cancel.
      </div>
    </div>,
    document.body
  );
};



